/**
 * Product 3D model: Three.js only. Renders into the existing canvas (canvas.clientWidth / canvas.clientHeight).
 * Requires import map and Three.js build + addons in assets. No Google model-viewer.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

function createThreeViewer(canvas, modelUrl, envMapUrl) {
  const container = canvas.parentElement;
  let width = canvas.clientWidth;
  let height = canvas.clientHeight;
  if (width === 0 || height === 0) {
    width = container ? container.clientWidth : 1;
    height = container ? container.clientHeight : 1;
  }
  if (width === 0) width = 1;
  if (height === 0) height = 1;

  const scene = new THREE.Scene();
  scene.background = null;

  /* Overlay labels (top-left badge + bottom hint) */
  const badge = document.createElement('div');
  badge.className = 'product-model-badge';
  badge.setAttribute('aria-hidden', 'true');
  badge.textContent = 'INTERACTIVE 3D';
  const hint = document.createElement('div');
  hint.className = 'product-model-hint';
  hint.setAttribute('aria-hidden', 'true');
  hint.textContent = 'Drag to rotate • Scroll to zoom';
  if (container) {
    container.appendChild(badge);
    container.appendChild(hint);
  }

  /* ——— Camera ———
   * PerspectiveCamera(fov, aspect, near, far)
   * - fov: vertical field of view in degrees (e.g. 45–75)
   * - position: camera placement; default (0, 0, 4) = in front of model
   */
  const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 2000);
  camera.position.set(0, 0, 1.5);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1;

  /* Lights: when env map is used, keep only a small fill so EXR drives the look */
  const useEnvMap = Boolean(envMapUrl);
  const ambient = new THREE.AmbientLight(0xffffff, useEnvMap ? 0.15 : 0.6);
  scene.add(ambient);
  const dir = new THREE.DirectionalLight(0xffffff, useEnvMap ? 0.2 : 0.8);
  dir.position.set(2, 2, 2);
  scene.add(dir);

  /* Pre-filtered / PMREM environment map for IBL (see Three.js docs) */
  let envMapRT = null;
  let pmrem = null;
  if (envMapUrl) {
    pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    const exrLoader = new EXRLoader();
    exrLoader.load(envMapUrl, (hdrTexture) => {
      hdrTexture.mapping = THREE.EquirectangularReflectionMapping;
      const rt = pmrem.fromEquirectangular(hdrTexture);
      const envMap = rt.texture;
      scene.environment = envMap;
      envMapRT = rt;
      hdrTexture.dispose();
    }, undefined, (err) => console.warn('EXR env map load failed:', err));
  }

  /* ——— OrbitControls (drag to rotate, scroll to zoom) ———
   * minDistance / maxDistance: zoom limits
   * target: point the camera looks at (model center)
   */
  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.minDistance = 0.5;
  controls.maxDistance = 10;
  controls.target.set(0, 0, 0);

  let animationId = null;
  let modelRoot = null;
  const clock = new THREE.Clock();

  const resize = () => {
    const w = canvas.clientWidth || (container && container.clientWidth) || 1;
    const h = canvas.clientHeight || (container && container.clientHeight) || 1;
    if (w === 0 || h === 0) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
  };

  const resizeObserver = new ResizeObserver(resize);
  if (container) resizeObserver.observe(container);

  function animate() {
    animationId = requestAnimationFrame(animate);
    const delta = clock.getDelta();
    if (modelRoot) modelRoot.rotation.y += 0.12 * delta;
    controls.update();
    renderer.render(scene, camera);
  }

  function centerAndScale(object) {
    const box = new THREE.Box3().setFromObject(object);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    object.position.sub(center);
    const maxDim = Math.max(size.x, size.y, size.z);
    if (maxDim > 0) {
      const scale = 1.5 / maxDim;
      object.scale.multiplyScalar(scale);
    }
  }

  const progressBar = container?.querySelector('.product-model-loading-progress');
  const loadingEl = container?.querySelector('.product-model-loading');

  function hideLoading() {
    if (container) container.classList.add('loaded');
  }

  if (loadingEl) loadingEl.classList.add('indeterminate');

  const loader = new GLTFLoader();
  loader.load(
    modelUrl,
    (gltf) => {
      hideLoading();
      try {
        const model = gltf.scene;
        if (!model) throw new Error('No scene in model');
        model.traverse((child) => {
          if (child.isMesh && child.material) child.material.depthWrite = true;
        });
        centerAndScale(model);
        modelRoot = model;
        scene.add(model);
        animate();
      } catch (e) {
        console.error('Product model init error:', e);
      }
    },
    (xhr) => {
      if (progressBar && xhr.lengthComputable && xhr.total > 0) {
        if (loadingEl) loadingEl.classList.remove('indeterminate');
        const pct = Math.round((xhr.loaded / xhr.total) * 100);
        progressBar.style.width = pct + '%';
      } else if (progressBar && xhr.loaded > 0) {
        if (loadingEl) loadingEl.classList.remove('indeterminate');
        progressBar.style.width = '85%';
      }
    },
    (err) => {
      hideLoading();
      console.error('Product model load error:', err);
    }
  );

  return {
    dispose() {
      if (animationId != null) cancelAnimationFrame(animationId);
      resizeObserver.disconnect();
      badge.remove();
      hint.remove();
      if (envMapRT) envMapRT.dispose();
      if (pmrem) pmrem.dispose();
      renderer.dispose();
    },
  };
}

function getDeferredMedia() {
  return customElements.get('deferred-media');
}

function defineProductModel() {
  const DeferredMedia = getDeferredMedia();
  if (!DeferredMedia || customElements.get('product-model')) return;
  customElements.define(
    'product-model',
    class ProductModel extends DeferredMedia {
      constructor() {
        super();
      }

      loadContent() {
        if (this.getAttribute('loaded')) return;
        const modelUrl = this.getAttribute('data-model-url');
        const envMapUrl = this.getAttribute('data-env-map') || null;
        const canvas = this.querySelector('.product-model-canvas');
        if (!modelUrl || !canvas) return;
        window.pauseAllMedia?.();
        const init = () => {
          if (this.getAttribute('loaded')) return;
          this._threeDispose = createThreeViewer(canvas, modelUrl, envMapUrl);
          if (this._threeDispose) this.setAttribute('loaded', true);
        };
        if (canvas.clientWidth && canvas.clientHeight) {
          init();
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(init);
          });
        }
      }

      connectedCallback() {
        const slide = this.closest('.product__media-item');
        if (slide && slide.classList.contains('is-active')) {
          requestAnimationFrame(() => this.loadContent());
        }
      }

      disconnectedCallback() {
        if (this._threeDispose) {
          this._threeDispose.dispose();
          this._threeDispose = null;
        }
      }
    }
  );
}

defineProductModel();
if (!customElements.get('product-model')) {
  document.addEventListener('DOMContentLoaded', defineProductModel);
}

window.ProductModel = {
  loadShopifyXR() {
    if (typeof Shopify === 'undefined' || !Shopify.loadFeatures) return;
    Shopify.loadFeatures([
      { name: 'shopify-xr', version: '1.0', onLoad: this.setupShopifyXR.bind(this) },
    ]);
  },
  setupShopifyXR(errors) {
    if (errors) return;
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR());
      return;
    }
    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  },
};

window.addEventListener('DOMContentLoaded', () => {
  if (window.ProductModel) window.ProductModel.loadShopifyXR();
});
